import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import contact_us from "../../Assets/headers/contact_us.png";
import Textfield from "../Textfield";

// Form validation schema
const VALIDATION_SCHEMA = Yup.object({
  firstname: Yup.string().required("First name is required"),
  lastname: Yup.string().required("Last name is required"),
  businessemail: Yup.string()
    .email("Invalid email")
    .required("Email is required"),
  companyname: Yup.string().required("Company name is required"),
  jobtitle: Yup.string(),
  companysize: Yup.string(),
  comments: Yup.string(),
});

// Input field configurations
const INPUT_FIELDS = [
  { name: "firstname", label: "First Name *", type: "text" },
  { name: "lastname", label: "Last Name *", type: "text" },
  { name: "businessemail", label: "Email *", type: "email" },
  { name: "companyname", label: "Company Name *", type: "text" },
  { name: "jobtitle", label: "Job Title", type: "text" },
  { name: "companysize", label: "Company Size", type: "text" },
  {
    name: "comments",
    label: "Share how many users you would like and any other questions?",
    type: "text",
    multiline: true,
    rows: 4,
  },
];

// Consolidated styles
const styles = {
  container: {
    maxWidth: {
      xs: "100%",
      sm: "90%",
      md: "80%",
      lg: "80%",
      xl: "75%",
    },
  },
  heroTitle: {
    fontFamily: "Roboto",
    fontWeight: "bold",
    fontSize: { xs: "2.5rem", md: "4rem" },
    color: "#1662A7",
    textAlign: "center",
  },
  subtitle: {
    fontFamily: "Roboto",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
    fontWeight: 450,
    color: "#25B2E8",
    textAlign: "center",
    mb: { xs: 4, md: 6 },
  },
  sectionTitle: {
    fontFamily: "Roboto",
    fontSize: { xs: "1.5rem", md: "2rem" },
    fontWeight: "bold",
    color: "#1662A7",
    mb: 2,
  },
  card: {
    backgroundColor: "#F3F4F6",
    border: "1px solid #B8C2CC",
    boxShadow: "4px 4px 20px rgba(0, 0, 0, 0.1)",
    borderRadius: "20px",
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 28px rgba(0, 0, 0, 0.15)",
    },
  },
  textField: {
    "& .MuiInputLabel-root": {
      color: "#8A8A8A",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#1662A7",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "white",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#25B2E8",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1662A7",
      },
    },
  },
  submitButton: {
    backgroundColor: "#25B2E8",
    fontFamily: "Roboto",
    height: "45px",
    borderRadius: "10px",
    fontSize: "18px",
    color: "white",
    textTransform: "capitalize",
    alignSelf: "center",
    width: "180px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#1662A7",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(37, 178, 232, 0.4)",
    },
  },
};

const fixedTopLabelProps = {
  shrink: true,
  disableAnimation: true,
};

const triggerConfetti = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const colors = ["#25B2E8", "#1662A7", "#7A5AF8", "#00C2A8", "#F59E0B"];
  const pieces = 140;

  for (let i = 0; i < pieces; i += 1) {
    const confetti = document.createElement("div");
    const size = Math.random() * 8 + 4;
    const startX = Math.random() * window.innerWidth;
    const drift = (Math.random() - 0.5) * 320;
    const rotate = Math.random() * 1080;
    const duration = 1800 + Math.random() * 1400;

    confetti.style.position = "fixed";
    confetti.style.left = `${startX}px`;
    confetti.style.top = "-16px";
    confetti.style.width = `${size}px`;
    confetti.style.height = `${size * 0.6}px`;
    confetti.style.background = colors[Math.floor(Math.random() * colors.length)];
    confetti.style.borderRadius = "2px";
    confetti.style.zIndex = "9999";
    confetti.style.pointerEvents = "none";
    confetti.style.opacity = "1";
    confetti.style.transform = `translate3d(0, 0, 0) rotate(0deg)`;
    confetti.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity ${duration}ms linear`;

    document.body.appendChild(confetti);

    requestAnimationFrame(() => {
      confetti.style.transform = `translate3d(${drift}px, ${window.innerHeight + 100}px, 0) rotate(${rotate}deg)`;
      confetti.style.opacity = "0";
    });

    setTimeout(() => {
      confetti.remove();
    }, duration + 100);
  }
};

// Contact Sales Form Component
const ContactSalesForm = ({ formik, inputFields, isSubmitted }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
    {isSubmitted ? (
      <Card sx={{ ...styles.card, width: "100%" }}>
        <CardContent sx={{ py: { xs: 6, md: 8 } }}>
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: "#22C55E",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "2.25rem",
                fontWeight: 700,
                lineHeight: 1,
                boxShadow: "0 8px 24px rgba(34, 197, 94, 0.35)",
              }}
            >
              ✓
            </Box>
            <Typography sx={{ ...styles.sectionTitle, textAlign: "center", mb: 0 }}>
              THANKS! Your message is on the way.
            </Typography>
            <Typography sx={{ ...styles.subtitle, mb: 0 }}>
              We will be in touch within 1 business day max.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    ) : (
      <>
        <Typography sx={{ ...styles.sectionTitle, textAlign: "center" }}>
          Contact Sales
        </Typography>
        <Typography sx={styles.subtitle}>
          We are ready to help you sign up or give you support.
        </Typography>
        <Card sx={{ ...styles.card, width: "100%" }}>
          <CardContent>
            <Box component="form" onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                {/* Status Message */}
                {formik.status && (
                  <Alert
                    severity={formik.status.type}
                    sx={{ borderRadius: "10px" }}
                  >
                    {formik.status.message}
                  </Alert>
                )}

                {/* First Name & Last Name Row */}
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  {inputFields.slice(0, 2).map((field) => (
                    <TextField
                      key={field.name}
                      fullWidth
                      {...field}
                      InputLabelProps={fixedTopLabelProps}
                      value={formik.values[field.name]}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[field.name] &&
                        Boolean(formik.errors[field.name])
                      }
                      helperText={
                        formik.touched[field.name] && formik.errors[field.name]
                      }
                      sx={styles.textField}
                      disabled={formik.isSubmitting}
                    />
                  ))}
                </Stack>

                {/* Business Email */}
                <TextField
                  fullWidth
                  {...inputFields[2]}
                  InputLabelProps={fixedTopLabelProps}
                  value={formik.values[inputFields[2].name]}
                  onChange={formik.handleChange}
                  error={
                    formik.touched[inputFields[2].name] &&
                    Boolean(formik.errors[inputFields[2].name])
                  }
                  helperText={
                    formik.touched[inputFields[2].name] &&
                    formik.errors[inputFields[2].name]
                  }
                  sx={styles.textField}
                  disabled={formik.isSubmitting}
                />

                {/* Company Name */}
                <TextField
                  fullWidth
                  {...inputFields[3]}
                  InputLabelProps={fixedTopLabelProps}
                  value={formik.values[inputFields[3].name]}
                  onChange={formik.handleChange}
                  error={
                    formik.touched[inputFields[3].name] &&
                    Boolean(formik.errors[inputFields[3].name])
                  }
                  helperText={
                    formik.touched[inputFields[3].name] &&
                    formik.errors[inputFields[3].name]
                  }
                  sx={styles.textField}
                  disabled={formik.isSubmitting}
                />

                {/* Job Title & Company Size Row (commented out as requested) */}
                {/*
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  {inputFields.slice(4, 6).map((field) => (
                    <TextField
                      key={field.name}
                      fullWidth
                      {...field}
                      value={formik.values[field.name]}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[field.name] &&
                        Boolean(formik.errors[field.name])
                      }
                      helperText={
                        formik.touched[field.name] && formik.errors[field.name]
                      }
                      sx={styles.textField}
                      disabled={formik.isSubmitting}
                    />
                  ))}
                </Stack>
                */}

                {/* Comments */}
                <TextField
                  fullWidth
                  {...inputFields[6]}
                  InputLabelProps={fixedTopLabelProps}
                  value={formik.values[inputFields[6].name]}
                  onChange={formik.handleChange}
                  error={
                    formik.touched[inputFields[6].name] &&
                    Boolean(formik.errors[inputFields[6].name])
                  }
                  helperText={
                    formik.touched[inputFields[6].name] &&
                    formik.errors[inputFields[6].name]
                  }
                  sx={styles.textField}
                  disabled={formik.isSubmitting}
                />

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    type="submit"
                    sx={styles.submitButton}
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </>
    )}
  </Box>
);

function Contactus() {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      setStatus(null);

      const response = await fetch(
        "https://server.requiresign.com/send-contact-form",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: values.firstname,
            lastName: values.lastname,
            email: values.businessemail,
            companyName: values.companyname,
            jobTitle: values.jobtitle,
            companySize: values.companysize,
            comments: values.comments,
          }),
        }
      );

      if (response.ok) {
        setStatus({ type: "success", message: "Form submitted successfully!" });
        setIsSubmitted(true);
        triggerConfetti();
        formik.resetForm();
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus({
        type: "error",
        message: "Failed to submit form. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      businessemail: "",
      companyname: "",
      jobtitle: "",
      companysize: "",
      comments: "",
    },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: handleSubmit,
  });

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          mt: { xs: 8, md: 10 },
          pt: { xs: 6, md: 12 },
          pb: { xs: 4, md: 8 },
          backgroundImage: `linear-gradient(rgba(22, 98, 167, 0.1), rgba(37, 178, 232, 0.05)), url(${contact_us})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: "35vh", md: "10vh" },
        }}
      >
        <Container maxWidth="lg" sx={styles.container}>
          <Typography variant="h1" sx={styles.heroTitle}>
            Contact
          </Typography>
        </Container>
      </Box>

      {/* Content Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg" sx={styles.container}>
          {/* Desktop Layout */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Grid container spacing={6} justifyContent="center">
              <Grid item xs={12} md={8} lg={6}>
                <ContactSalesForm
                  formik={formik}
                  inputFields={INPUT_FIELDS}
                  isSubmitted={isSubmitted}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Mobile Layout */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Stack spacing={6}>
              <ContactSalesForm
                formik={formik}
                inputFields={INPUT_FIELDS}
                isSubmitted={isSubmitted}
              />
            </Stack>
          </Box>

          {/* Email Signup */}
          <Box sx={{ mt: { xs: 6, md: 10 } }}>
            <Textfield />
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default Contactus;
